import type { Tutorial, TutorialChapter, UpcomingTutorial } from "./types";

/** The course's sections, in order. Topics not listed here go last. */
export const TUTORIAL_TOPICS = [
  "Getting Started",
  "Case Prep",
  "Firm Knowledge",
  "Clients"
];

/**
 * Every tutorial. Adding one here is how it ships: each user in its audience
 * gets a toast card for it once on their next visit, and it stays under
 * Tutorials in the account menu.
 */
export const TUTORIALS: Tutorial[] = [
  {
    id: "new-caseblink-2026-10",
    title: "Welcome to the New CaseBlink",
    description:
      "A tour of what's new, then a case prepared with Casey, from an empty folder to a filing packet.",
    topic: "Getting Started",
    publishedAt: "2026-10-27",
    src: "https://firebasestorage.googleapis.com/v0/b/caseblink-prod.appspot.com/o/public%2Ftutorials%2FCaseBlink_Tutorial.mp4?alt=media&token=b22f23e4-b233-4ad5-bb95-2eafbc0a3905",
    poster: "/tutorials/caseblink-new-user-tutorial.jpg",
    durationSecs: 544,
    audience: ["Lawyer"],
    chapters: [
      { title: "Welcome and What's New", start: 0 },
      { title: "Tour", start: 39.5 },
      { title: "Settings", start: 183.7 },
      { title: "Firm Knowledge", start: 222.9 },
      { title: "Case Prep With Casey", start: 269.9 },
      { title: "Wrap-Up", start: 524.8 }
    ]
  }
];

/**
 * Tutorials in production, listed as Coming Soon on the Tutorials page. When
 * one ships, move it to TUTORIALS under the same id.
 */
export const UPCOMING_TUTORIALS: UpcomingTutorial[] = [
  {
    id: "drafting-with-casey",
    title: "Drafting and Reviewing With Casey",
    topic: "Case Prep",
    audience: ["Lawyer"]
  },
  {
    id: "uscis-forms",
    title: "Filling USCIS Forms",
    topic: "Case Prep",
    audience: ["Lawyer"]
  },
  {
    id: "firm-knowledge",
    title: "Building Your Firm Knowledge",
    topic: "Firm Knowledge",
    audience: ["Lawyer"]
  },
  {
    id: "intakes-client-portal",
    title: "Intakes and the Client Portal",
    topic: "Clients",
    audience: ["Lawyer"]
  }
];

/** The tutorials for `userType`, newest first. */
export const tutorialsFor = (
  userType: string,
  all: Tutorial[] = TUTORIALS
): Tutorial[] =>
  all
    .filter((t) => t.audience.includes(userType))
    .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));

/** The upcoming tutorials for `userType`, minus any that have shipped. */
export const upcomingFor = (
  userType: string,
  shipped: Tutorial[],
  all: UpcomingTutorial[] = UPCOMING_TUTORIALS
): UpcomingTutorial[] =>
  all.filter(
    (u) => u.audience.includes(userType) && !shipped.some((t) => t.id === u.id)
  );

const topicRank = (topic: string): number => {
  const i = TUTORIAL_TOPICS.indexOf(topic);
  return i === -1 ? TUTORIAL_TOPICS.length : i;
};

export type CourseSection = {
  topic: string;
  tutorials: Tutorial[];
  upcoming: UpcomingTutorial[];
};

/**
 * The course: a section per topic in TUTORIAL_TOPICS order, each with its
 * tutorials oldest first, then what's coming.
 */
export const courseSections = (
  tutorials: Tutorial[],
  upcoming: UpcomingTutorial[] = []
): CourseSection[] =>
  Array.from(new Set([...tutorials, ...upcoming].map((t) => t.topic)))
    .sort((a, b) => topicRank(a) - topicRank(b))
    .map((topic) => ({
      topic,
      tutorials: tutorials
        .filter((t) => t.topic === topic)
        .sort((a, b) => a.publishedAt.localeCompare(b.publishedAt)),
      upcoming: upcoming.filter((u) => u.topic === topic)
    }));

/** The newest tutorial for `userType` that isn't in `seen`. */
export const nextUnseenTutorial = (
  userType: string,
  seen: string[],
  all: Tutorial[] = TUTORIALS
): Tutorial | undefined =>
  tutorialsFor(userType, all).find((t) => !seen.includes(t.id));

/** Index of the chapter playing at `time`. */
export const chapterAt = (chapters: TutorialChapter[], time: number): number =>
  chapters.reduce((at, c, i) => (time >= c.start ? i : at), 0);

/** 0:00, 4:29, 1:02:05. */
export const formatTimestamp = (secs: number): string => {
  const total = Math.max(0, Math.floor(secs));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
};

const NEW_FOR_DAYS = 30;

/** Badged "New" for a month after it's released. */
export const isNewTutorial = (t: Tutorial, now = Date.now()): boolean =>
  now - Date.parse(t.publishedAt) < NEW_FOR_DAYS * 24 * 3600 * 1000;
